import React from 'react';
import { Bot, Globe, Database, Server } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Section, SectionHeader } from '../../../../components/shared/Section';
import { Card, CardHeader, CardContent } from '../../../../components/shared/Card';
import { cn } from '../../../../lib/utils';
import { getThemeClass } from '../../../../lib/theme';

const domains = [
  {
    title: "AI Platforms & Agentic Systems",
    description: "Multi-LLM metadata intelligence, direct-authored page building, and edge-native agent research",
    icon: Bot,
    achievements: [
      {
        metric: "26,000+",
        label: "Standard Components",
        description: "Discovery and quality analysis alongside ~1M custom components"
      },
      {
        metric: "GPT, Claude, Gemini",
        label: "Multi-LLM Platform",
        description: "LLM-as-a-Judge evaluation with fault-tolerant processing"
      }
    ],
    href: "/journey#ai-platforms"
  },
  {
    title: "Edge Computing & Content Delivery",
    description: "Architected global CDN infrastructure enabling Fortune 500 digital transformations through sophisticated edge computing and optimization",
    icon: Globe,
    achievements: [
      {
        metric: "Cloudflare & Akamai",
        label: "Multi-CDN Architecture",
        description: "Edge delivery and optimization at enterprise scale"
      }
    ],
    href: "/journey#edge-computing"
  },
  {
    title: "Distributed Systems",
    description: "Revolutionized event processing and data indexing architectures",
    icon: Database,
    achievements: [
      {
        metric: "5ms",
        label: "Indexing Latency",
        description: "Down from 15 minutes"
      },
      {
        metric: "85%",
        label: "API Performance",
        description: "Through innovative caching"
      }
    ],
    href: "/journey#distributed-systems"
  },
  {
    title: "Platform Architecture",
    description: "Architected end-to-end subscription and payment platforms powering major streaming services",
    icon: Server,
    achievements: [
      {
        metric: "Full-Stack",
        label: "Architecture",
        description: "End-to-end subscription management"
      },
      {
        metric: "6+",
        label: "Payment Methods",
        description: "Global payment processing integration"
      },
      {
        metric: "Multi-Platform",
        label: "Deployment",
        description: "Web, mobile, smart TV & OTT platforms"
      }
    ],
    href: "/journey#platform-architecture"
  }
];

const DomainPreview = () => {
  return (
    <Section 
      background="secondary" 
      spacing="sm"
      className={cn(
        getThemeClass('colors.background.light'),
        'relative'
      )}
      withDivider={true}
    >
      <div className={cn(
        'absolute inset-0',
        getThemeClass('gradients.page.main'),
        'opacity-50'
      )} />
      
      <div className="relative">
        <SectionHeader
          title="Domain Expertise"
          subtitle="Specialized knowledge and achievements across key technical domains"
          align="center"
          className={getThemeClass('colors.text.primary', 'text')}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          {domains.map((domain, idx) => (
            <Link key={idx} to={domain.href} className="block h-full">
              <Card
                variant="default"
                size="lg"
                className={cn(
                  'h-full transition-all duration-300',
                  'hover:shadow-lg',
                  getThemeClass('colors.border.hover', 'borderHover')
                )}
              >
                <CardHeader
                  icon={domain.icon}
                  title={domain.title}
                  subtitle={domain.description}
                  iconClassName={cn(
                    'w-8 h-8',
                    getThemeClass('colors.primary.main', 'text')
                  )}
                />
                <CardContent>
                  <div className="space-y-4">
                    {domain.achievements.map((achievement, i) => (
                      <div
                        key={i}
                        className={cn(
                          'border-l-4 pl-4',
                          getThemeClass('colors.primary.light', 'borderHover')
                        )}
                      >
                        <div className={cn(
                          'text-2xl font-bold',
                          getThemeClass('colors.primary.main', 'text')
                        )}>
                          {achievement.metric}
                        </div>
                        <div className={cn(
                          'text-sm font-medium',
                          getThemeClass('colors.text.primary', 'text')
                        )}>
                          {achievement.label}
                        </div>
                        <div className={cn(
                          'text-sm',
                          getThemeClass('colors.text.secondary', 'text')
                        )}>
                          {achievement.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default DomainPreview;
